import { Dialog, DialogTitle, DialogContent, Button, DialogActions, Box } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FieldError, FormProvider, useForm } from 'react-hook-form';
import { FormTextField, SelectField } from 'shared/controls';
import { useEditCustomer } from 'shared/mutations/edit-customer';
import { useQueryCustomerTiers, useQueryCustomerChannels, useQueryCustomerLookupWithFilters } from 'shared/queries';
import { CustomerLookup, CustomerNameChange } from 'shared/types';
import { useSnackbar } from 'notistack';
import { useCreateCustomer } from 'shared/mutations';
import { useService } from 'shared/providers';
import { useAuthState } from 'shared/stores';
import UpdateNameChangeConfirmation from './update-name-change-confirmation';
import { useDeleteDraftNameChange } from 'shared/mutations/delete-name-change';

interface EditCustomerDialogProps {
  isOpen: boolean;
  toggleDialog: () => void;
  currentCustomer: CustomerLookup;
}

export function EditCustomerDialog({ isOpen, toggleDialog, currentCustomer }: EditCustomerDialogProps) {
  const resolveUserDecisionRef = useRef<((result: boolean) => void) | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [draftCurrentCustomerName, setdraftCurrentCustomerName] = useState('');
  const [draftNewCustomerName, setdraftNewCustomerName] = useState('');
  const defaultValues = currentCustomer;
  const { enqueueSnackbar } = useSnackbar();
  const authState = useAuthState();
  const user = authState.user.get();
  const methods = useForm<CustomerLookup>({
    defaultValues: defaultValues,
  });
  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    reset({ ...defaultValues });
  }, [isOpen, reset, defaultValues]);

  const onClose = useCallback(() => {
    toggleDialog();
  }, [toggleDialog]);

  const customerMutationDelete = useEditCustomer();
  const customerMutationCreate = useCreateCustomer();
  const deleteDraftNameChangeMutation = useDeleteDraftNameChange();

  const { draftNameChangeService } = useService();

  const { isFetching: isFetchingCustomers, isLoading: isLoadingCustomers } = useQueryCustomerLookupWithFilters({
    abgBrand: currentCustomer.brand,
    customerTier: currentCustomer.customertier,
    channel: currentCustomer.channel,
    masterCustomer: currentCustomer.abgmastercustomer,
  });

  const handleConfirmation = (): Promise<boolean> => {
    return new Promise(resolve => {
      setShowConfirmationModal(true);
      resolveUserDecisionRef.current = resolve;
    });
  };

  const createNameChange = async (editedCustomer: CustomerLookup) => {
    editedCustomer.uniqueid = editedCustomer.abgmastercustomer + editedCustomer.brand + editedCustomer.channel;

    const newCustomerNameChange: CustomerNameChange = {
      id: editedCustomer.uniqueid,
      currentCustomerName: currentCustomer.abgmastercustomer,
      newCustomerName: editedCustomer.abgmastercustomer.toUpperCase(),
      brand: editedCustomer.brand,
      channel: editedCustomer.channel,
      customerTier: editedCustomer.customertier,
      modifiedBy: user?.displayName || '',
      modifiedByEmail: '',
      modifiedDate: new Date().toISOString(),
      approvedBy: '',
      approvedDate: '',
      approvalStatus: 'Pending Approval',
    };
    await draftNameChangeService.createNewDraftNameChange(newCustomerNameChange);
    enqueueSnackbar('Customer name change has been submitted for approval', { variant: 'success' });
  };

  const onSubmit = async (editedCustomer: CustomerLookup) => {
    //First check if user has actually changed anything
    if (
      editedCustomer.channel !== currentCustomer.channel ||
      editedCustomer.customertier !== currentCustomer.customertier ||
      editedCustomer.abgmastercustomer !== currentCustomer.abgmastercustomer
    ) {
      //Then if there is a change, check to see what attributes has changed. This if else block is in order of complexity
      if (editedCustomer.abgmastercustomer !== currentCustomer.abgmastercustomer) {
        //If at least the master customer name has changed, we will need to create a new customer since the unique id will change
        //We will also need to delete the old customer as well as transfer any contracts over to the newly created customer
        //Then, if there is an existing customer already, ask user if they want to proceed
        const nameChangeAlreadyExistsForMasterName = await draftNameChangeService.getNameChangeDataForMasterCustomerName(
          currentCustomer.abgmastercustomer
        );
        if (nameChangeAlreadyExistsForMasterName?.some(obj => obj.approvalStatus === 'Pending Approval')) {
          const currentNameChangeApproval = nameChangeAlreadyExistsForMasterName.find(
            obj => obj.approvalStatus === 'Pending Approval'
          );
          setdraftNewCustomerName(currentNameChangeApproval?.newCustomerName || '');
          setdraftCurrentCustomerName(currentNameChangeApproval?.currentCustomerName || '');

          const userAgreed = await handleConfirmation();
          if (userAgreed) {
            createNameChange(editedCustomer);
            deleteDraftNameChangeMutation.mutateAsync(currentNameChangeApproval!.id);
          }
        } else {
          createNameChange(editedCustomer);
        }
      } else if (editedCustomer.channel !== currentCustomer.channel) {
        //If the master name has not change but the channel changed, create new customer and delete old customer
        editedCustomer.uniqueid = editedCustomer.abgmastercustomer + editedCustomer.brand + editedCustomer.channel;
        await customerMutationCreate.mutateAsync(editedCustomer);
        await customerMutationDelete.mutateAsync(currentCustomer);
      }
      //If channel and master name is the same, then tier must have changed, can just do a regular save since unique id remains the same
      else {
        await customerMutationCreate.mutateAsync(editedCustomer);
      }
    } else {
      enqueueSnackbar('Customer did not change!', { variant: 'success' });
    }
    toggleDialog();
  };
  const { tierOptions, isFetching: isFetchingTiers, isLoading: isLoadingTiers } = useQueryCustomerTiers();
  const { channelOptions, isFetching: isFetchingChannels, isLoading: isLoadingChannels } = useQueryCustomerChannels();
  const isLoading =
    isFetchingChannels ||
    isLoadingChannels ||
    isFetchingTiers ||
    isLoadingTiers ||
    isFetchingCustomers ||
    isLoadingCustomers;

  return (
    <Dialog open={isOpen && !isLoading} onClose={onClose} fullWidth aria-labelledby='edit-customer-dialog'>
      <DialogTitle id='edit-customer-dialog-title'>{'Edit Customer'}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id='edit-customer-form' onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ marginTop: 1 }}>
              {' '}
              <FormTextField
                name='abgmastercustomer'
                label='Customer'
                fullWidth
                error={Boolean(errors?.abgmastercustomer)}
                helperText={errors?.abgmastercustomer?.message}
                rules={{
                  required: { message: 'Customer is required', value: true },
                }}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <FormTextField
                disabled
                name='brand'
                label='Brand'
                error={Boolean(errors?.brand)}
                helperText={(errors?.brand as FieldError | undefined)?.message}
                rules={{
                  required: { message: 'Brand is required', value: true },
                }}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='customertier'
                label='Tier'
                loading={isFetchingTiers || isLoadingTiers}
                options={tierOptions || []}
                getOptionLabel={opt => opt}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                error={Boolean(errors?.customertier)}
                helperText={errors?.customertier?.message}
                rules={{
                  required: { message: 'Customer Tier is required', value: true },
                }}
              />
            </Box>
            <Box sx={{ marginTop: 1 }}>
              <SelectField
                name='channel'
                label='Channel'
                loading={isFetchingChannels || isLoadingChannels}
                options={channelOptions || []}
                getOptionLabel={opt => opt}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return true;
                  return option === value;
                }}
                error={Boolean(errors?.channel)}
                helperText={errors?.channel?.message}
                rules={{
                  required: { message: 'Channel is required', value: true },
                }}
              />
            </Box>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Cancel
        </Button>
        <Button type='submit' form='edit-customer-form' variant='contained' color='primary'>
          Submit
        </Button>
      </DialogActions>
      {showConfirmationModal && (
        <UpdateNameChangeConfirmation
          isOpen={showConfirmationModal}
          onDialogDecision={result => {
            if (resolveUserDecisionRef.current) {
              resolveUserDecisionRef.current(result);
            }
            setShowConfirmationModal(false);
          }}
          currentCustomer={draftCurrentCustomerName}
          newAbgMasterName={draftNewCustomerName}
        ></UpdateNameChangeConfirmation>
      )}
    </Dialog>
  );
}
